// /opt/isp_erp/frontend/src/pages/ftth/RedMapa.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  MapContainer, TileLayer, Marker, Popup, Polyline,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Box, Card, CardContent, Chip, Stack, Typography, CircularProgress,
  Button, FormControl, InputLabel, Select, MenuItem, TextField, Alert,
  IconButton, Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import { ftthGeoAPI } from '../../api/ftth_geo.api';

// --- Iconos por tipo (divIcon, sin depender de imágenes) ---
const makeIcon = (color) => L.divIcon({
  className: '',
  html: `<div style="background:${color};width:16px;height:16px;border-radius:50%;border:2px solid white;box-shadow:0 0 4px rgba(0,0,0,.6)"></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

const ICONS = {
  OLT: makeIcon('#e91e63'),
  SPLITTER: makeIcon('#9c27b0'),
  NAP: makeIcon('#2196f3'),
  POSTE: makeIcon('#795548'),
  CAJA_EMPALME: makeIcon('#ff5722'),
  RESERVA: makeIcon('#607d8b'),
  CLIENTE: makeIcon('#4caf50'),
};

const COLOR_CABLE = {
  FEEDER: '#e91e63',
  DISTRIBUCION: '#ff9800',
  DROP: '#4caf50',
};

export default function RedMapa() {
  const [data, setData] = useState({ elementos: [], cables: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [center, setCenter] = useState([-12.05, -77.05]); // Lima, ajusta
  const [mapKey, setMapKey] = useState(0);

  const cargar = async () => {
    setLoading(true);
    setError('');
    try {
      const d = await ftthGeoAPI.getTopologia();
      setData(d);
      // Auto-centrar en el primer elemento
      if (d.elementos?.length) {
        setCenter([d.elementos[0].latitud, d.elementos[0].longitud]);
        setMapKey(k => k + 1);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cargar topología');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const elementosFiltrados = useMemo(() => {
    let list = data.elementos || [];
    if (filtroTipo) list = list.filter(e => e.tipo === filtroTipo);
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase();
      list = list.filter(e =>
        (e.codigo || '').toLowerCase().includes(q) ||
        (e.nombre || '').toLowerCase().includes(q) ||
        (e.direccion || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [data.elementos, filtroTipo, busqueda]);

  const idVisibles = useMemo(
    () => new Set(elementosFiltrados.map(e => e.id)),
    [elementosFiltrados]
  );

  const cablesFiltrados = useMemo(() => {
    if (!filtroTipo && !busqueda.trim()) return data.cables || [];
    return (data.cables || []).filter(
      c => idVisibles.has(c.origen_id) || idVisibles.has(c.destino_id)
    );
  }, [data.cables, filtroTipo, busqueda, idVisibles]);

  return (
    <Box sx={{ position: 'relative', height: 'calc(100vh - 80px)' }}>
      {/* ---- Panel superior ---- */}
      <Card sx={{
        position: 'absolute', zIndex: 1000, top: 10, left: 10, right: 10,
        maxWidth: 900,
      }}>
        <CardContent sx={{ pb: '12px !important' }}>
          <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
            <Typography variant="h6" sx={{ mr: 1 }}>🌐 Red FTTH</Typography>

            <Chip size="small" color="secondary" label={`OLTs: ${data.stats?.total_olts ?? 0}`} />
            <Chip size="small" label={`Splitters: ${data.stats?.total_splitters ?? 0}`} />
            <Chip size="small" color="primary" label={`NAPs: ${data.stats?.total_naps ?? 0}`} />
            <Chip size="small" label={`Postes: ${data.stats?.total_postes ?? 0}`} />
            <Chip size="small" color="success" label={`Km: ${data.stats?.km_totales ?? 0}`} />

            <Box sx={{ flexGrow: 1 }} />

            <Button
              size="small"
              variant="contained"
              startIcon={<UploadFileIcon />}
              href="/ftth/red/importar"
            >
              Importar KMZ/CSV
            </Button>
            <Tooltip title="Recargar">
              <IconButton size="small" onClick={cargar}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Centrar en red">
              <IconButton
                size="small"
                onClick={() => {
                  if (elementosFiltrados.length) {
                    setCenter([elementosFiltrados[0].latitud, elementosFiltrados[0].longitud]);
                    setMapKey(k => k + 1);
                  }
                }}
              >
                <CenterFocusStrongIcon />
              </IconButton>
            </Tooltip>
          </Stack>

          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Tipo</InputLabel>
              <Select
                value={filtroTipo}
                label="Tipo"
                onChange={(e) => setFiltroTipo(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="OLT">OLT</MenuItem>
                <MenuItem value="SPLITTER">Splitter</MenuItem>
                <MenuItem value="NAP">NAP / CTO</MenuItem>
                <MenuItem value="POSTE">Poste</MenuItem>
                <MenuItem value="CAJA_EMPALME">Caja Empalme</MenuItem>
                <MenuItem value="RESERVA">Reserva</MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Buscar código / nombre / dirección"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              sx={{ flexGrow: 1, minWidth: 240 }}
            />
          </Stack>

          {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
        </CardContent>
      </Card>

      {/* ---- Mapa ---- */}
      {loading ? (
        <Box display="flex" alignItems="center" justifyContent="center" height="100%">
          <CircularProgress />
        </Box>
      ) : (
        <MapContainer
          key={mapKey}
          center={center}
          zoom={14}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; OpenStreetMap contributors'
          />

          {/* Cables (líneas) */}
          {cablesFiltrados.map((c) => {
            const coords = c.ruta_geojson?.coordinates || [];
            if (coords.length < 2) return null;
            const positions = coords.map(([lng, lat]) => [lat, lng]);
            return (
              <Polyline
                key={`c-${c.id}`}
                positions={positions}
                color={COLOR_CABLE[c.tipo] || '#555'}
                weight={2}
              >
                <Popup>
                  <b>Cable {c.tipo}</b><br />
                  Código: {c.codigo}<br />
                  Longitud: {Math.round(c.longitud_m)} m<br />
                  Hilos: {c.hilos_usados}/{c.hilos_totales}
                </Popup>
              </Polyline>
            );
          })}

          {/* Elementos (puntos) */}
          {elementosFiltrados.map((el) => (
            <Marker
              key={`e-${el.id}`}
              position={[el.latitud, el.longitud]}
              icon={ICONS[el.tipo] || ICONS.NAP}
            >
              <Popup>
                <b>{el.tipo}</b><br />
                <b>{el.codigo}</b><br />
                {el.nombre && <>{el.nombre}<br /></>}
                {el.direccion && <>{el.direccion}<br /></>}
                {el.capacidad > 0 && <>Capacidad: {el.usados}/{el.capacidad}<br /></>}
                <small>Origen: {el.origen}</small>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      )}

      {/* ---- Leyenda (HTML puro, NO react-leaflet) ---- */}
      <Card sx={{ position: 'absolute', bottom: 20, right: 20, zIndex: 1000 }}>
        <CardContent sx={{ py: '8px !important', px: 2 }}>
          <Typography variant="caption" fontWeight="bold">Leyenda</Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
            {[
              ['OLT', '#e91e63'],
              ['Splitter', '#9c27b0'],
              ['NAP', '#2196f3'],
              ['Poste', '#795548'],
            ].map(([label, color]) => (
              <Stack key={label} direction="row" spacing={0.5} alignItems="center">
                <span
                  style={{
                    display: 'inline-block',
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    background: color,
                    border: '2px solid white',
                    boxShadow: '0 0 3px rgba(0,0,0,.5)',
                  }}
                />
                <Typography variant="caption">{label}</Typography>
              </Stack>
            ))}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
